import { CostBasisMethodHolding } from '@interfaces/cost-basis/cost-basis.service.interface';
import {
  InternalTransferMethodEnum,
  InternalTransferTypeEnum,
} from '@interfaces/internal-transfer/internal-transfer.interface';
import { TransactionTypesEnum } from '@interfaces/models';
import { AvgCostConversion, MinTaxConversion } from '@interfaces/models/spec-id-fallback.enum';
import { MultiITState as _MultiITState, MultiITToAccount } from '@interfaces/state';
import { createEntityAdapter, EntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { AccountTypes, CostBasisMethods } from '@shared/models';
import { CostBasisPosition } from '@shared/services';

import { StartOverStateCleared } from '../../actions/start-over/start-over.actions';
import * as MultiITActions from '../actions/multi-internal-transfer.actions';

export interface MultiITState extends _MultiITState {}

export const multiITToAdapter: EntityAdapter<MultiITToAccount> =
  createEntityAdapter<MultiITToAccount>({
    selectId: (account: MultiITToAccount) => account.transactionId,
    sortComparer: false,
  });

export const initialState: MultiITState = {
  triage: {
    transferFrom: AccountTypes.EMPTY,
    transferTo: [],
  },
  transferFrom: {
    selectedAccount: null,
    selectedHoldings: [],
    transferMethod: InternalTransferMethodEnum.UNKNOWN,
    transferType: InternalTransferTypeEnum.UNKNOWN,
    splitEvenly: false,
    avgCostConversion: AvgCostConversion.EMPTY,
    minTaxConversion: MinTaxConversion.EMPTY,
  },
  transferTo: multiITToAdapter.getInitialState(),
  oaCollection: {
    currentlyCollecting: null,
    completedAccounts: [],
  },
};

const itmReducer = createReducer(
  initialState,

  on(MultiITActions.UpdateMultiITTriage, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      ...payload,
    },
  })),

  on(MultiITActions.UpdateMultiITFrom, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      ...payload,
    },
  })),

  on(MultiITActions.SetMultiITFromAccount, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      selectedAccount: payload,
    },
  })),

  on(MultiITActions.SetMultiITSelectedHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      selectedHoldings: payload,
    },
  })),

  on(MultiITActions.SetMultiITTransferMethod, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      transferMethod: payload,
    },
  })),

  on(MultiITActions.AddMultiITToAccount, (state, { payload }) => ({
    ...state,
    transferTo: multiITToAdapter.addOne(payload, state.transferTo),
  })),

  on(MultiITActions.UpdateMultiITToAccount, (state, { id, payload }) => ({
    ...state,
    transferTo: multiITToAdapter.updateOne({ id, changes: payload }, state.transferTo),
  })),

  on(MultiITActions.RemoveMultiITToAccount, (state, { payload }) => ({
    ...state,
    transferTo: multiITToAdapter.removeOne(payload, state.transferTo),
  })),

  on(MultiITActions.ClearMultiITToAccounts, (state) => ({
    ...state,
    transferTo: multiITToAdapter.removeAll(state.transferTo),
  })),

  on(MultiITActions.SetMultiITMXM, (state, { payload }) => ({
    ...state,
    mxm: {
      ...payload,
    },
  })),

  on(MultiITActions.ClearMultiITState, () => ({ ...initialState })),
  on(StartOverStateCleared, () => ({ ...initialState })),

  on(MultiITActions.ClearMultiITToPositions, (state) => {
    const allAccounts = Object.values(state.transferTo.entities);
    const updates = allAccounts
      .filter((account): account is MultiITToAccount => account !== undefined)
      .map((account) => ({
        id: account.transactionId,
        changes: { positions: [] },
      }));
    return {
      ...state,
      transferTo: multiITToAdapter.updateMany(updates, state.transferTo),
    };
  }),

  on(MultiITActions.LoadMultiITCostBasisMethodSuccess, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      selectedHoldings: addCostBasisToSelectedHoldings(
        state.transferFrom.selectedHoldings,
        payload,
      ),
    },
  })),

  on(MultiITActions.ApplyMultiITCostBasisShownMethod, (state) => {
    const { avgCostConversion, minTaxConversion } = state.transferFrom;

    const updates = Object.values(state.transferTo.entities)
      .filter(
        (account): account is MultiITToAccount =>
          account !== undefined &&
          account.positions.some(
            (position) =>
              position.selectedCostBasisMethod === CostBasisMethods.AVERAGE_COST ||
              position.selectedCostBasisMethod === CostBasisMethods.MIN_TAX,
          ),
      )
      .map((account) => ({
        id: account.transactionId,
        changes: {
          positions: account.positions.map((position) => {
            if (position.selectedCostBasisMethod === CostBasisMethods.AVERAGE_COST) {
              return {
                ...position,
                shownCostBasisMethod:
                  avgCostConversion === AvgCostConversion.FIFO
                    ? CostBasisMethods.FIFO
                    : CostBasisMethods.AVERAGE_COST,
              };
            }
            if (position.selectedCostBasisMethod === CostBasisMethods.MIN_TAX) {
              return {
                ...position,
                shownCostBasisMethod:
                  minTaxConversion === MinTaxConversion.FIFO
                    ? CostBasisMethods.FIFO
                    : CostBasisMethods.MIN_TAX,
              };
            }
            return position;
          }),
        },
      }));

    return {
      ...state,
      transferTo: multiITToAdapter.updateMany(updates, state.transferTo),
    };
  }),

  // OA Collection Management
  on(MultiITActions.StartNewAccountCollection, (state, { transactionId }) => ({
    ...state,
    oaCollection: {
      completedAccounts: state.oaCollection.completedAccounts ?? [],
      currentlyCollecting: transactionId,
    },
  })),

  on(MultiITActions.CompleteNewAccountCollection, (state, { transactionId }) => {
    const existingCompleted = state.oaCollection.completedAccounts ?? [];
    const completedAccounts = existingCompleted.includes(transactionId)
      ? existingCompleted
      : [...existingCompleted, transactionId];

    return {
      ...state,
      oaCollection: {
        currentlyCollecting: state.oaCollection.currentlyCollecting,
        completedAccounts,
      },
    };
  }),

  on(MultiITActions.UpdateCurrentlyCollecting, (state) => {
    const newAccountIds = (state.transferTo.ids as string[]).filter(
      (id) => state.transferTo.entities[id]?.transactionType === TransactionTypesEnum.NEW_ACCOUNT,
    );
    const completedIds = state.oaCollection.completedAccounts ?? [];
    const nextId = newAccountIds.find((id) => !completedIds.includes(id));

    return {
      ...state,
      oaCollection: {
        completedAccounts: completedIds,
        currentlyCollecting: nextId ?? null,
      },
    };
  }),

  on(MultiITActions.ClearOACollectionState, (state) => ({
    ...state,
    oaCollection: {
      currentlyCollecting: null,
      completedAccounts: [],
    },
  })),

  // Per-Account OA Data Updates
  on(MultiITActions.UpdateITMToAccountObjectives, (state, { id, objectives }) => {
    const existingAccount = state.transferTo.entities[id];
    if (!existingAccount) return state;

    return {
      ...state,
      transferTo: multiITToAdapter.updateOne(
        {
          id,
          changes: {
            openAccount: {
              ...(existingAccount.openAccount ?? {}),
              objectives,
              settlementFund: existingAccount.openAccount?.settlementFund ?? null,
            },
          },
        },
        state.transferTo,
      ),
    };
  }),

  on(MultiITActions.UpdateITMToAccountSettlementFund, (state, { id, settlementFund }) => {
    const existingAccount = state.transferTo.entities[id];
    if (!existingAccount) return state;

    return {
      ...state,
      transferTo: multiITToAdapter.updateOne(
        {
          id,
          changes: {
            openAccount: {
              ...(existingAccount.openAccount ?? {}),
              objectives: existingAccount.openAccount?.objectives ?? null,
              settlementFund,
            },
          },
        },
        state.transferTo,
      ),
    };
  }),

  on(MultiITActions.SetMultiITToAccount, (state, { payload }) => {
    return {
      ...state,
      transferTo: {
        ids: payload.ids,
        entities: payload.entities,
      },
    };
  }),
);

export function reducer(state: MultiITState | undefined, action: Action) {
  return itmReducer(state, action);
}

const addCostBasisToSelectedHoldings = (
  selectedHoldings: CostBasisPosition[],
  costBasisMethodHoldings: CostBasisMethodHolding[],
): CostBasisPosition[] => {
  if (!costBasisMethodHoldings?.length) {
    return selectedHoldings;
  }
  const holdingsMap = new Map(
    costBasisMethodHoldings
      .filter((h) => h?.holdingIdentifier != null)
      .map((h) => [h.holdingIdentifier.toString(), h]),
  );
  return selectedHoldings.map((position) => {
    const costBasisMethod = holdingsMap.get(position.positionId);
    const newPosition = { ...position };
    if (costBasisMethod) {
      if (!newPosition.selectedCostBasisMethod) {
        newPosition.selectedCostBasisMethod = costBasisMethod.costBasisMethodCode;
      }
      newPosition.shownCostBasisMethod = costBasisMethod.costBasisMethodCode;
    }
    return newPosition;
  });
};
